import { Button } from '@repo/ui/components/button';
import { cn } from '@repo/ui/lib/utils';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
	children: ReactNode;
	/** Optional className for the fallback wrapper. */
	fallbackClassName?: string;
	/** Optional override for the "Try again" handler. Defaults to `window.location.reload()`. */
	onReset?: () => void;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
}

/**
 * Top-level React error boundary.
 *
 * React 19 still leans on class components for `componentDidCatch` /
 * `getDerivedStateFromError`, so we keep the class shape. The fallback
 * is intentionally minimal and centered: an icon, a short message, and
 * a "Try again" button that re-mounts the children by clearing the
 * `hasError` flag (or, by default, performs a hard reload so any
 * corrupted in-memory state is also cleared).
 *
 * Errors are logged to the console so the existing observability stack
 * picks them up; route-level error handling can compose with this by
 * rendering their own `ErrorComponent` from the router.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	override state: ErrorBoundaryState = { hasError: false, error: null };

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { hasError: true, error };
	}

	override componentDidCatch(error: Error, info: ErrorInfo): void {
		// Surface to the dev console — production monitoring (Sentry, etc.)
		// can hook in here later without changing the boundary shape.
		console.error('[ErrorBoundary] Caught error:', error, info.componentStack);
	}

	private handleReset = (): void => {
		if (this.props.onReset) {
			this.props.onReset();
			return;
		}
		if (typeof window !== 'undefined') {
			window.location.reload();
		}
	};

	override render(): ReactNode {
		const { hasError, error } = this.state;
		const { children, fallbackClassName } = this.props;

		if (!hasError) return children;

		return (
			<div
				role='alert'
				aria-live='assertive'
				className={cn(
					'flex flex-1 flex-col items-center justify-center gap-4 px-6 py-16 text-center',
					'animate-in fade-in-0 slide-in-from-bottom-1 duration-300 motion-reduce:animate-none',
					fallbackClassName,
				)}
			>
				<div className='bg-destructive/10 text-destructive ring-destructive/20 flex size-12 items-center justify-center rounded-full ring-1'>
					<AlertTriangle className='size-5' aria-hidden='true' />
				</div>

				<div className='space-y-1'>
					<h1 className='text-foreground text-lg font-semibold tracking-tight'>Something went wrong</h1>
					<p className='text-muted-foreground max-w-sm text-sm'>
						An unexpected error broke this view. <br /> If the problem keeps showing up, please contact support.
					</p>
				</div>

				{error?.message ? (
					<pre className='text-muted-foreground bg-muted/40 max-w-md overflow-x-auto rounded-md px-3 py-2 text-left font-mono text-xs'>
						{error.message}
					</pre>
				) : null}

				<Button onClick={this.handleReset} variant='default' size='sm' className='mt-2'>
					<RotateCw className='size-3.5' aria-hidden='true' />
					Try again
				</Button>
			</div>
		);
	}
}
